/**
 * Session-scoped, per-picker "last folder" memory for every file/folder dialog
 * in the app.
 *
 * Mirrors the C++ behaviour: each picker keeps its own module-level
 * `*_initial_dir` variable that is seeded from a default folder the first time
 * it opens and updated to the directory of the user's last selection. Those
 * variables live only for the process lifetime, so on every restart each picker
 * re-seeds from its default folder. We reproduce that here with an in-memory
 * `Map` keyed by `PickerKey` — it resets when the app (the JS context) restarts,
 * and keys are fully independent, so the Restore folder never inherits the
 * Select-Data-File folder, etc.
 *
 * Default folders match C++:
 *   - data pickers   → get_default_tiger_data_directory(config)   (data dir)
 *   - output pickers → get_default_output_directory()             (Desktop)
 */
import { invoke } from "@tauri-apps/api/core";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Stable keys for every picker, so a typo can't silently merge two pickers. */
export const PickerKey = {
  // data-dir pickers
  selectDataFile: "selectDataFile",
  restore: "restore",
  backup: "backup",
  dataBrowse: "dataBrowse",
  // output-dir (Desktop) pickers
  exportData: "exportData",
  graphExportData: "graphExportData",
  graphPng: "graphPng",
  xyGraphExportData: "xyGraphExportData",
  xyGraphPng: "xyGraphPng",
  rightPanePng: "rightPanePng",
  diagnosticsReport: "diagnosticsReport",
  loadToolSet: "loadToolSet",
  saveToolSet: "saveToolSet",
  importCsd: "importCsd",
  controllerReport: "controllerReport",
  stabilityReport: "stabilityReport",
  toolReport: "toolReport",
  eventReport: "eventReport",
  excelReport: "excelReport",
  xmlReport: "xmlReport",
} as const;
export type PickerKey = (typeof PickerKey)[keyof typeof PickerKey];

/** Directory of each picker's last selection, this session only. */
const lastDirs = new Map<string, string>();

/** Parent directory of a path (strips the trailing leaf). Handles `\` and `/`. */
export function dirname(path: string): string {
  const i = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
  return i > 0 ? path.slice(0, i) : "";
}

/** Join a directory and a leaf using the directory's own separator. */
function joinPath(dir: string, name: string): string {
  if (!dir) return name;
  const sep = dir.includes("\\") ? "\\" : "/";
  return dir.endsWith(sep) ? dir + name : dir + sep + name;
}

async function defaultDataDir(config: string): Promise<string | undefined> {
  if (!isTauri()) return undefined;
  try {
    const dir = await invoke<string>("get_default_data_directory", { config });
    return dir || undefined;
  } catch {
    return undefined;
  }
}

async function defaultOutputDir(): Promise<string | undefined> {
  if (!isTauri()) return undefined;
  try {
    const dir = await invoke<string>("get_default_output_directory");
    return dir || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Initial directory for a picker's `defaultPath`: the folder remembered for this
 * key this session, else the default folder. Pass `config` for a data-dir
 * picker (seeds from the turbine's data directory); omit it for an output
 * picker (seeds from the Desktop).
 */
export async function pickerInitialDir(
  key: string,
  config?: string,
): Promise<string | undefined> {
  const remembered = lastDirs.get(key);
  if (remembered) return remembered;
  return config != null ? defaultDataDir(config) : defaultOutputDir();
}

/**
 * `defaultPath` for a save dialog: `<initial dir>/<filename>`. Keeps the
 * caller's suggested filename while steering the dialog to the remembered /
 * default folder.
 */
export async function pickerSavePath(
  key: string,
  filename: string,
  config?: string,
): Promise<string> {
  const dir = await pickerInitialDir(key, config);
  return dir ? joinPath(dir, filename) : filename;
}

/**
 * Record the folder of a selection so this picker reopens there next time.
 * Pass `isDirectory` for folder pickers (the path *is* the directory);
 * otherwise the filename is stripped. A `null`/empty selection is ignored.
 */
export function rememberPickerDir(
  key: string,
  selected: string | string[] | null | undefined,
  isDirectory = false,
): void {
  const first = Array.isArray(selected) ? selected[0] : selected;
  if (!first) return;
  const dir = isDirectory ? first : dirname(first);
  if (dir) lastDirs.set(key, dir);
}

/** Test-only: clear the remembered folders. */
export function _resetPickerDirs(): void {
  lastDirs.clear();
}
